import { useCallback, useState } from "react";
import { useQueryClient } from '@common-fe/common-fe';

import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';
import useLinkLmnToClaimQuery from '@/modules/MyDocuments/queries/useLinkLmnToClaim.query';
import { GET_TRANSACTION_DETAILS_BY_CLAIM_ID } from '@/modules/transaction/hooks/useGetTransactionDetails.query';

interface Props {
  claimId?: string;
  onSuccess?: () => void;
}

const useLinkLmnToClaim = ({ claimId, onSuccess }: Props) => {
  const queryClient = useQueryClient();
  const { handleAddPermanentSnackbar } = useSnackbar();
  const [selectedLmnId, setSelectedLmnId] = useState<string>();
  const { linkLmnToClaim, isLinkLmnToClaimLoading } = useLinkLmnToClaimQuery();

  const handleSuccess = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [GET_TRANSACTION_DETAILS_BY_CLAIM_ID, claimId],
    });
    onSuccess?.();
  }, [queryClient, claimId, onSuccess]);

  const handleLinkLmnToClaim = useCallback(async () => {
    if (!claimId || !selectedLmnId) {
      return;
    }
    await linkLmnToClaim({
      claimId,
      lmnId: selectedLmnId,
      updateClaimStatus: true
    });
    handleAddPermanentSnackbar({
      text: "Document added. The claim is about to be processed.",
      state: SnackbarState.neutral,
      closeIcon: true,
      autoRemove: true,
    });
    handleSuccess();
  }, [selectedLmnId, claimId, handleSuccess, linkLmnToClaim, handleAddPermanentSnackbar]);
  
  const handleSelectLmn = useCallback((lmnId: string) => {
    if (lmnId === selectedLmnId) {
      setSelectedLmnId(undefined);
      return;
    }

    setSelectedLmnId(lmnId);
  }, [selectedLmnId]);

  return {
    selectedLmnId,
    handleSelectLmn,
    handleLinkLmnToClaim,
    isLinkLmnToClaimLoading,
  };
};

export default useLinkLmnToClaim;
